import { Controller } from "@hotwired/stimulus"
import { plainNumber, writeClipboard } from "../lib/hover_copy"

// The details panel's files (paperwork_processes/_files).
//
// A thumbnail's real preview fades in over its paper skeleton once the image
// has loaded; one that fails keeps the skeleton, still. Images already in the
// cache are marked at once, so a redraw never flashes the skeleton.
//
// Every file sits in a folder: a tab per folder, and always one folder open
// (the first to begin with). A tab, or an arrow key along the tabs, opens its
// folder in place of the last.
//
// The panel is redrawn by morphing (PaperworkProcess#broadcast_files). A
// morph would reset the folder someone had opened, so those attributes are
// left alone. After a morph that adds or
// removes folders, the one chosen stays open, or the first if it is gone.
//
// Usage: <div data-controller="file-shelf"> with thumbnails as
// data-file-shelf-target="thumb", their <img> calling #loaded / #failed, and
// folder tabs (aria-controls naming their folder) calling #selectFolder
// and, on keydown, #moveFolder.
export default class extends Controller {
  static targets = ["thumb"]

  connect() {
    this.keepState = this.keepState.bind(this)
    this.afterMorph = this.afterMorph.bind(this)
    this.element.addEventListener("turbo:before-morph-attribute", this.keepState)
    this.element.addEventListener("turbo:morph-element", this.afterMorph)
    if (typeof ResizeObserver !== "undefined") {
      this.resizing = new ResizeObserver(() => this.markAllTabEdges())
      this.resizing.observe(this.element)
    }
    this.markAllTabEdges()
  }

  disconnect() {
    this.element.removeEventListener("turbo:before-morph-attribute", this.keepState)
    this.element.removeEventListener("turbo:morph-element", this.afterMorph)
    this.resizing?.disconnect()
  }

  // A row of tabs wider than the panel fades at the edge with more to see.
  markTabEdges(event) {
    const row = event.currentTarget
    const start = row.scrollLeft > 1
    const end = row.scrollWidth - row.clientWidth - row.scrollLeft > 1
    row.dataset.overflow = [start && "start", end && "end"].filter(Boolean).join(" ")
  }

  markAllTabEdges() {
    this.element.querySelectorAll(".file-folders__tabs").forEach((row) => this.markTabEdges({ currentTarget: row }))
  }

  selectFolder(event) {
    this.openFolder(event.currentTarget)
  }

  moveFolder(event) {
    const tabs = this.folderTabs(event.currentTarget)
    const index = tabs.indexOf(event.currentTarget)
    const next = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: tabs.length - 1 }[event.key]
    if (next === undefined) return
    event.preventDefault()
    const tab = tabs[(next + tabs.length) % tabs.length]
    this.openFolder(tab)
    tab.focus()
  }

  openFolder(tab, { scroll = true } = {}) {
    this.chosenFolder = tab.getAttribute("aria-controls")
    this.folderTabs(tab).forEach((other) => {
      const open = other === tab
      other.setAttribute("aria-selected", String(open))
      other.setAttribute("tabindex", open ? "0" : "-1")
      const folder = document.getElementById(other.getAttribute("aria-controls"))
      if (folder) folder.hidden = !open
    })
    if (scroll) tab.scrollIntoView?.({ block: "nearest", inline: "nearest" })
  }

  folderTabs(tab) {
    return Array.from(tab.parentElement?.querySelectorAll(".file-folder__tab") || [])
  }

  // Exactly one folder open per row of tabs: the one chosen if it is still
  // there, else the one already open, else the first.
  settleFolders() {
    this.settling = false
    this.element.querySelectorAll(".file-folders__tabs").forEach((row) => {
      const tabs = Array.from(row.querySelectorAll(".file-folder__tab"))
      const tab = tabs.find((each) => each.getAttribute("aria-controls") === this.chosenFolder) ||
        tabs.find((each) => each.getAttribute("aria-selected") === "true") || tabs[0]
      if (tab) this.openFolder(tab, { scroll: false })
    })
    this.markAllTabEdges()
  }

  thumbTargetConnected(thumb) {
    this.refreshThumb(thumb)
  }

  refreshThumb(thumb) {
    const image = thumb.querySelector("img")
    if (thumb.classList.contains("is-busy") || thumb.classList.contains("is-failed")) {
      thumb.classList.remove("is-ready")
    } else if (image) {
      thumb.classList.toggle("is-ready", image.complete && image.naturalWidth > 0)
    } else if (!thumb.querySelector(".file-thumb__real")) {
      thumb.classList.remove("is-ready")
    }
  }

  afterMorph(event) {
    // An unchanged image URL neither reloads nor reconnects its target, but
    // the morph has replaced the class that recorded its loaded state.
    if (event.target.matches?.('[data-file-shelf-target~="thumb"]')) this.refreshThumb(event.target)
    // Once the morph is done, before the page is painted.
    if (!this.settling && event.target.closest?.(".file-folders")) {
      this.settling = true
      queueMicrotask(() => this.settleFolders())
    }
  }

  // A key fact chip copies its value. It sits inside the tile's link, so the
  // click copies instead of opening the file. Shift-click on an amount copies
  // the plain number, as the hover-copy pill does.
  async copyFact(event) {
    event.preventDefault()
    event.stopPropagation()
    const chip = event.currentTarget
    const shown = (chip.dataset.hoverCopyText || chip.textContent).trim()
    const text = event.shiftKey ? (plainNumber(shown) || shown) : shown
    try {
      await writeClipboard(document, text)
      chip.classList.add("is-copied")
      clearTimeout(chip.copiedTimer)
      chip.copiedTimer = setTimeout(() => chip.classList.remove("is-copied"), 1200)
    } catch (error) {
      console.error("Copy failed:", error)
    }
  }

  loaded(event) {
    const thumb = event.target.closest(".file-thumb")
    if (thumb) this.refreshThumb(thumb)
  }

  failed(event) {
    const thumb = event.target.closest(".file-thumb")
    event.target.remove()
    thumb?.classList.remove("is-ready")
    thumb?.classList.add("is-still")
  }

  keepState(event) {
    const { attributeName } = event.detail || {}
    const target = event.target
    if (["aria-selected", "tabindex"].includes(attributeName) && target.matches?.(".file-folder__tab")) event.preventDefault()
    if (attributeName === "hidden" && target.matches?.(".file-folder")) event.preventDefault()
    if (attributeName === "data-overflow" && target.matches?.(".file-folders__tabs")) event.preventDefault()
  }
}
